import React, { useEffect, useState } from 'react';
import {
  ColorPricingSettings,
  Product,
  ProductCollection,
  ProductCollectionType
} from '../../../types';
import {
  CAKE_COLORS,
  CakeColor,
  createDefaultColorPricingSettings,
  formatColorPriceInput,
  getBaseColorAdditionalPrice,
  getCollectionColorPrice,
  getGlobalColorPrice,
  parseColorPriceInput
} from '../../../constants/cakeColors';
import {
  getCollections,
  getColorPricingSettings,
  getProducts,
  initializeDefaultColorPricingSettings,
  updateColorPricingSettings
} from '../../../services/firebase';
import { Button } from '../../atoms';
import './ColorPricingManagement.css';

type InnerTab = 'Global' | ProductCollectionType;

type ProductPriceDraft = {
  productId: string;
  price: string;
};

const euro = (amount: number): string => `€${amount.toFixed(2)}`;

const FALLBACK_COLLECTION_TABS: ProductCollectionType[] = [
  'Everyday Cakes',
  'Cake & Cupcake Set',
  'Celebration Cakes',
  'Custom Cake Flavours',
  'Create Your Own Cake',
  'Wedding Cakes',
  'Desserts'
];

const emptyColourDraft = (): Record<string, string> =>
  CAKE_COLORS.reduce<Record<string, string>>((acc, colour) => {
    acc[colour.name] = '';
    return acc;
  }, {});

const draftsFromSettings = (settings: ColorPricingSettings) => {
  const globalDraft = emptyColourDraft();
  Object.entries(settings.globalPrices || {}).forEach(([name, price]) => {
    globalDraft[name] = formatColorPriceInput(price);
  });

  const collectionDraft: Record<string, Record<string, string>> = {};
  Object.entries(settings.collectionPrices || {}).forEach(([collection, prices]) => {
    collectionDraft[collection] = emptyColourDraft();
    Object.entries(prices || {}).forEach(([name, price]) => {
      collectionDraft[collection][name] = formatColorPriceInput(price);
    });
  });

  const productDraft: Record<string, Record<string, ProductPriceDraft[]>> = {};
  Object.entries(settings.productPrices || {}).forEach(([collection, colours]) => {
    productDraft[collection] = {};
    Object.entries(colours || {}).forEach(([colour, products]) => {
      productDraft[collection][colour] = Object.entries(products || {}).map(
        ([productId, price]) => ({
          productId,
          price: formatColorPriceInput(price)
        })
      );
    });
  });

  return { globalDraft, collectionDraft, productDraft };
};

const compactPriceMap = (draft: Record<string, string>): Record<string, number> =>
  Object.entries(draft).reduce<Record<string, number>>((acc, [name, raw]) => {
    const parsed = parseColorPriceInput(raw);
    if (parsed !== null) acc[name] = parsed;
    return acc;
  }, {});

const ColourSwatchLabel: React.FC<{ color: CakeColor }> = ({ color }) => (
  <span className="color-pricing__swatch-label">
    <span
      className="color-pricing__swatch"
      style={{ backgroundColor: color.value, borderColor: color.border }}
      aria-hidden="true"
    />
    {color.name}
  </span>
);

const ColorPricingManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedSettings, setSavedSettings] = useState<ColorPricingSettings | null>(null);
  const [collections, setCollections] = useState<ProductCollection[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [tab, setTab] = useState<InnerTab>('Global');
  const [selectedColour, setSelectedColour] = useState(CAKE_COLORS[0].name);
  const [globalDraft, setGlobalDraft] = useState<Record<string, string>>(emptyColourDraft);
  const [collectionDraft, setCollectionDraft] = useState<Record<string, Record<string, string>>>({});
  const [productDraft, setProductDraft] = useState<Record<string, Record<string, ProductPriceDraft[]>>>({});
  const [pendingProductId, setPendingProductId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [settingsData, collectionData, productData] = await Promise.all([
          getColorPricingSettings(),
          getCollections(),
          getProducts()
        ]);

        const settings =
          settingsData ??
          (await initializeDefaultColorPricingSettings().catch(() =>
            createDefaultColorPricingSettings()
          ));
        if (cancelled) return;

        setSavedSettings(settings);
        setCollections(collectionData);
        setProducts(productData);
        const drafts = draftsFromSettings(settings);
        setGlobalDraft(drafts.globalDraft);
        setCollectionDraft(drafts.collectionDraft);
        setProductDraft(drafts.productDraft);
      } catch (err) {
        console.error('Error loading colour pricing settings:', err);
        if (!cancelled) {
          setError('Failed to load colour pricing. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const draftSettings = (): ColorPricingSettings => {
    const collectionPrices = Object.entries(collectionDraft).reduce<
      ColorPricingSettings['collectionPrices']
    >((acc, [collection, prices]) => {
      const compact = compactPriceMap(prices);
      if (Object.keys(compact).length > 0) {
        acc[collection as ProductCollectionType] = compact;
      }
      return acc;
    }, {});

    const productPrices = Object.entries(productDraft).reduce<
      ColorPricingSettings['productPrices']
    >((acc, [collection, colours]) => {
      const colourMap = Object.entries(colours).reduce<Record<string, Record<string, number>>>(
        (colourAcc, [colour, rows]) => {
          const productMap = rows.reduce<Record<string, number>>((productAcc, row) => {
            const parsed = parseColorPriceInput(row.price);
            if (parsed !== null) productAcc[row.productId] = parsed;
            return productAcc;
          }, {});
          if (Object.keys(productMap).length > 0) {
            colourAcc[colour] = productMap;
          }
          return colourAcc;
        },
        {}
      );
      if (Object.keys(colourMap).length > 0) {
        acc[collection as ProductCollectionType] = colourMap;
      }
      return acc;
    }, {});

    return {
      id: 'default',
      globalPrices: compactPriceMap(globalDraft),
      collectionPrices,
      productPrices,
      createdAt: savedSettings?.createdAt ?? new Date(),
      updatedAt: new Date()
    };
  };

  const liveSettings = draftSettings();
  const selectedColor = CAKE_COLORS.find((color) => color.name === selectedColour) ?? CAKE_COLORS[0];
  const collectionTabs =
    collections.length > 0
      ? collections.map((collection) => collection.name)
      : FALLBACK_COLLECTION_TABS;
  const isCollectionTab = tab !== 'Global';
  const collectionProducts = isCollectionTab
    ? products.filter((product) => product.collection === tab)
    : [];
  const assignedProducts = isCollectionTab
    ? productDraft[tab]?.[selectedColour] ?? []
    : [];
  const availableProducts = collectionProducts.filter(
    (product) => !assignedProducts.some((row) => row.productId === product.id)
  );

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const next = draftSettings();
      await updateColorPricingSettings({
        globalPrices: next.globalPrices,
        collectionPrices: next.collectionPrices,
        productPrices: next.productPrices
      });
      setSavedSettings(next);
    } catch (err) {
      console.error('Error saving colour pricing settings:', err);
      setError('Failed to save colour pricing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!savedSettings) return;
    const drafts = draftsFromSettings(savedSettings);
    setGlobalDraft(drafts.globalDraft);
    setCollectionDraft(drafts.collectionDraft);
    setProductDraft(drafts.productDraft);
    setPendingProductId('');
  };

  const setCollectionColourPrice = (collection: ProductCollectionType, colour: string, value: string) => {
    setCollectionDraft((current) => ({
      ...current,
      [collection]: {
        ...(current[collection] || emptyColourDraft()),
        [colour]: value
      }
    }));
  };

  const addProductPrice = () => {
    if (!isCollectionTab || !pendingProductId) return;
    setProductDraft((current) => {
      const existing = current[tab]?.[selectedColour] ?? [];
      if (existing.some((row) => row.productId === pendingProductId)) return current;
      return {
        ...current,
        [tab]: {
          ...(current[tab] || {}),
          [selectedColour]: [...existing, { productId: pendingProductId, price: '' }]
        }
      };
    });
    setPendingProductId('');
  };

  const updateProductPrice = (productId: string, value: string) => {
    if (!isCollectionTab) return;
    setProductDraft((current) => ({
      ...current,
      [tab]: {
        ...(current[tab] || {}),
        [selectedColour]: (current[tab]?.[selectedColour] ?? []).map((row) =>
          row.productId === productId ? { ...row, price: value } : row
        )
      }
    }));
  };

  const removeProductPrice = (productId: string) => {
    if (!isCollectionTab) return;
    setProductDraft((current) => ({
      ...current,
      [tab]: {
        ...(current[tab] || {}),
        [selectedColour]: (current[tab]?.[selectedColour] ?? []).filter(
          (row) => row.productId !== productId
        )
      }
    }));
  };

  if (loading) {
    return (
      <div className="color-pricing">
        <div className="color-pricing__loading">Loading colour pricing...</div>
      </div>
    );
  }

  return (
    <div className="color-pricing">
      <div className="color-pricing__header">
        <div>
          <h3>Colour Pricing</h3>
          <p>
            Global defaults apply everywhere. Each collection inherits Global unless you
            set a price. Assign a product only when one cake should differ.
          </p>
        </div>
        <div className="color-pricing__actions">
          <Button variant="secondary" size="small" onClick={handleReset} disabled={saving}>
            Discard changes
          </Button>
          <Button variant="primary" size="small" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save colour prices'}
          </Button>
        </div>
      </div>

      {error ? <div className="color-pricing__error">{error}</div> : null}

      <div className="color-pricing__tabs" role="tablist" aria-label="Colour pricing levels">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'Global'}
          className={`color-pricing__tab${tab === 'Global' ? ' is-active' : ''}`}
          onClick={() => setTab('Global')}
        >
          Global
        </button>
        {collectionTabs.map((collection) => (
          <button
            type="button"
            role="tab"
            aria-selected={tab === collection}
            className={`color-pricing__tab${tab === collection ? ' is-active' : ''}`}
            onClick={() => setTab(collection)}
            key={collection}
          >
            {collection}
          </button>
        ))}
      </div>

      {tab === 'Global' ? (
        <div className="color-pricing__panel">
          <p className="color-pricing__hint">
            Blank means that colour is free unless a collection or product sets a price.
          </p>
          <div className="color-pricing__table-wrap">
            <table className="color-pricing__table">
              <thead>
                <tr>
                  <th>Colour</th>
                  <th>Global price</th>
                  <th>Charged unless overridden</th>
                </tr>
              </thead>
              <tbody>
                {CAKE_COLORS.map((color) => (
                  <tr key={color.name}>
                    <td>
                      <ColourSwatchLabel color={color} />
                    </td>
                    <td>
                      <input
                        className="color-pricing__input"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={globalDraft[color.name] ?? ''}
                        onChange={(event) =>
                          setGlobalDraft((current) => ({
                            ...current,
                            [color.name]: event.target.value
                          }))
                        }
                        aria-label={`${color.name} global price`}
                      />
                    </td>
                    <td>{euro(getGlobalColorPrice(color.name, liveSettings))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="color-pricing__collection">
          <p className="color-pricing__hint">
            {tab} inherits from Global. Select a colour to set its collection price or pin
            a different amount on one product.
          </p>
          <div className="color-pricing__split">
            <div className="color-pricing__table-wrap">
              <table className="color-pricing__table">
                <thead>
                  <tr>
                    <th>Colour</th>
                    <th>From Global</th>
                    <th>Collection price</th>
                    <th>Charged</th>
                    <th>Products</th>
                  </tr>
                </thead>
                <tbody>
                  {CAKE_COLORS.map((color) => {
                    const productCount = productDraft[tab]?.[color.name]?.length ?? 0;
                    return (
                      <tr
                        key={color.name}
                        className={color.name === selectedColour ? 'is-selected' : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="color-pricing__colour-button"
                            onClick={() => setSelectedColour(color.name)}
                          >
                            <ColourSwatchLabel color={color} />
                          </button>
                        </td>
                        <td>{euro(getGlobalColorPrice(color.name, liveSettings))}</td>
                        <td>
                          <input
                            className="color-pricing__input"
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            placeholder="Inherit"
                            value={collectionDraft[tab]?.[color.name] ?? ''}
                            onChange={(event) =>
                              setCollectionColourPrice(tab, color.name, event.target.value)
                            }
                            aria-label={`${color.name} collection price`}
                          />
                        </td>
                        <td>
                          {euro(getCollectionColorPrice(color.name, tab, liveSettings))}
                        </td>
                        <td>{productCount > 0 ? productCount : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <aside className="color-pricing__detail">
              <h4>{selectedColor.name}</h4>
              <p>
                Collection price charged for products without their own row:{' '}
                <strong>
                  {euro(getCollectionColorPrice(selectedColor.name, tab, liveSettings))}
                </strong>
              </p>
              <label className="color-pricing__field">
                Collection price
                <input
                  className="color-pricing__input"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  placeholder={`Inherit ${euro(getGlobalColorPrice(selectedColor.name, liveSettings))}`}
                  value={collectionDraft[tab]?.[selectedColor.name] ?? ''}
                  onChange={(event) =>
                    setCollectionColourPrice(tab, selectedColor.name, event.target.value)
                  }
                />
              </label>

              <h5>Product prices</h5>
              <p className="color-pricing__hint">
                Use this only when one cake in {tab} should charge a different{' '}
                {selectedColor.name} amount.
              </p>

              {assignedProducts.length > 0 ? (
                <ul className="color-pricing__product-list">
                  {assignedProducts.map((row) => {
                    const productName =
                      products.find((product) => product.id === row.productId)?.name ||
                      row.productId;
                    return (
                      <li key={row.productId} className="color-pricing__product-row">
                        <span>{productName}</span>
                        <input
                          className="color-pricing__input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          placeholder={`Use ${euro(getCollectionColorPrice(selectedColor.name, tab, liveSettings))}`}
                          value={row.price}
                          onChange={(event) =>
                            updateProductPrice(row.productId, event.target.value)
                          }
                          aria-label={`${productName} ${selectedColor.name} price`}
                        />
                        <button
                          type="button"
                          className="color-pricing__remove"
                          onClick={() => removeProductPrice(row.productId)}
                          aria-label={`Remove ${productName}`}
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {availableProducts.length > 0 ? (
                <div className="color-pricing__assign">
                  <select
                    className="color-pricing__select"
                    value={pendingProductId}
                    onChange={(event) => setPendingProductId(event.target.value)}
                    aria-label={`Choose a product in ${tab}`}
                  >
                    <option value="">Choose a product in this collection</option>
                    {availableProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={addProductPrice}
                    disabled={!pendingProductId}
                  >
                    Assign price to product
                  </Button>
                </div>
              ) : null}

              {collectionProducts.length > 0 ? (
                <div className="color-pricing__preview">
                  <h5>What a shopper pays for {selectedColor.name}</h5>
                  <ul>
                    {collectionProducts.map((product) => (
                      <li key={product.id}>
                        {product.name}
                        {' · '}
                        {euro(
                          getBaseColorAdditionalPrice(selectedColor.name, {
                            collection: tab,
                            productId: product.id,
                            settings: liveSettings
                          })
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPricingManagement;
